import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQueryWithReauth } from '../baseQuery';
import { 
  IConversationListResponse, 
  IConversation,
  IAccount,
  IConversationQueryParams,
  IMessageResidentsQueryParams,
  IMessageResidentsResponse,
  IApiResponse,
} from '@/types/admin/messages';

// Helper function to convert query params to URLSearchParams (matching admin-web exactly)
export const syncHookFormAsQuery = (params: Record<string, string | undefined>) => {
  const searchParams = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== '') {
      searchParams.append(key, value);
    }
  });
  return searchParams;
};

// RTK Query API slice for messages
export const messagesApi = createApi({
  reducerPath: 'messagesApi',
  baseQuery: baseQueryWithReauth,
  tagTypes: ['Conversations', 'MessageResidents'],
  endpoints: (builder) => ({
    // List message residents (for creating conversations)
    listMessageResidents: builder.query<IMessageResidentsResponse, URLSearchParams>({
      query: (searchParams: URLSearchParams) => ({
        url: `/api/v1/messages/residents/?${searchParams.toString()}`,
        method: 'GET',
      }),
      providesTags: ['MessageResidents'],
    }),

    // Create conversation
    createConversation: builder.mutation<IApiResponse<IConversation>, FormData>({
      query: (body: FormData) => ({
        url: '/api/v1/messages/create-conversation',
        method: 'POST',
        body,
        formData: true,
      }),
      invalidatesTags: ['Conversations'],
    }),

    // Edit conversation
    editConversation: builder.mutation<IApiResponse<IConversation>, { body: FormData; conversationId: number }>({
      query: ({ body, conversationId }) => ({
        url: `/api/v1/messages/edit-conversation/${conversationId}`,
        method: 'PUT',
        body,
        formData: true,
      }),
      invalidatesTags: ['Conversations'],
    }),

    // List conversations
    listConversations: builder.query<IConversationListResponse, string>({
      query: (queryString: string) => ({
        url: `/api/v1/messages/conversations/?${queryString}`,
        method: 'GET',
      }),
      providesTags: ['Conversations'],
    }),

    // Get single conversation
    getConversation: builder.query<IApiResponse<IConversation>, { conversationId: number; queryParam?: URLSearchParams }>({
      query: ({ conversationId, queryParam }) => ({
        url: `/api/v1/messages/conversation/${conversationId}${queryParam ? `?${queryParam.toString()}` : ''}`,
        method: 'GET',
      }),
      providesTags: (result, error, { conversationId }) => [
        'Conversations',
        { type: 'Conversations', id: conversationId },
      ],
    }),

    // Archive conversation
    archiveConversation: builder.mutation<IApiResponse<{ archived: boolean }>, number>({
      query: (conversationId: number) => ({
        url: `/api/v1/messages/archive/${conversationId}`,
        method: 'PATCH',
      }),
      invalidatesTags: ['Conversations'],
    }),

    // Invite resident
    // inviteResident: builder.mutation<IApiResponse<any>, FormData>({
    //   query: (body: FormData) => ({
    //     url: '/api/v1/messages/invite-resident',
    //     method: 'POST',
    //     body,
    //     formData: true,
    //   }),
    // }),

    // Assign admins
    assignAdmins: builder.mutation<IApiResponse<IConversation>, FormData>({
      query: (body: FormData) => ({
        url: '/api/v1/messages/assign-admins',
        method: 'POST',
        body,
        formData: true,
      }),
      invalidatesTags: ['Conversations'],
    }),

    // Send message
    sendMessage: builder.mutation<IApiResponse<any>, { body: FormData; conversationId: number }>({
      query: ({ body, conversationId }) => ({
        url: `/api/v1/messages/send-message/${conversationId}`,
        method: 'POST',
        body,
        formData: true,
      }),
      invalidatesTags: (result, error, { conversationId }) => [
        'Conversations',
        { type: 'Conversations', id: conversationId },
      ],
    }),

    // Mark conversation as read
    markConversationAsRead: builder.mutation<IApiResponse<void>, number>({
      query: (conversationId) => ({
        url: `/api/v1/messages/conversation/${conversationId}/read`,
        method: 'PATCH',
      }),
      // Optimistic update: immediately update cache before API response
      async onQueryStarted(conversationId, { dispatch, queryFulfilled, getState }) {
        // Optimistically decrement the unread count
        const patchUnreadCount = dispatch(
          messagesApi.util.updateQueryData('getUnreadConversationsCount', undefined, (draft) => {
            if (draft.count > 0) {
              draft.count -= 1;
            }
          })
        );

        try {
          await queryFulfilled;
        } catch {
          // Revert optimistic update on error
          patchUnreadCount.undo();
        }
      },
      // Still invalidate to ensure eventual consistency
      invalidatesTags: ['Conversations'],
    }),

    // Get unread conversations count
    getUnreadConversationsCount: builder.query<{ success: boolean; count: number }, void>({
      query: () => ({
        url: '/api/v1/messages/unread-count',
        method: 'GET',
      }),
      providesTags: ['Conversations'],
    }),
  }),
});

// Export hooks for usage in functional components
export const {
  useLazyListMessageResidentsQuery,
  useLazyListConversationsQuery,
  useListConversationsQuery,
  useCreateConversationMutation,
  useLazyGetConversationQuery,
  useGetConversationQuery,
  useArchiveConversationMutation,
  useSendMessageMutation,
  // useInviteResidentMutation,
  useAssignAdminsMutation,
  useEditConversationMutation,
  useMarkConversationAsReadMutation,
  useGetUnreadConversationsCountQuery,
} = messagesApi;
